import { byOrder, computeTransform, measure, quadToStage } from './layout.js';
import { applyTransform, createStage, renderMasks, renderOutlines, setBackground, } from './stage.js';
/**
 * WebGL2 が使えない環境向けの描画([#33](../../../docs/issues/33-viewer-embed/DESIGN.md))。
 *
 * **背景画像と枠の輪郭だけを出す。** 動画は載せられないが、
 * 「どこに何が入るか」は伝わる —— 何も出ないより判断できる。
 *
 * **落とさないことが目的。** 埋め込み先は不特定の端末で開かれる(設計書 7.5)
 * ため、WebGL2 がない環境を「動かない」で済ませられない。
 */
export class FallbackRenderer {
    fit;
    container = null;
    stage = null;
    template = null;
    options = null;
    transform = { width: 0, height: 0, offsetX: 0, offsetY: 0 };
    highlighted = new Set();
    resizeObserver = null;
    constructor(fit = 'contain') {
        this.fit = fit;
    }
    mount(container, template, opts) {
        this.container = container;
        this.template = template;
        this.options = opts;
        this.stage = createStage(container);
        this.resizeObserver = new ResizeObserver(() => {
            this.resize();
        });
        this.resizeObserver.observe(container);
        this.resize();
    }
    /** GL が無いので上限は分からない。**判定しない**(0)。 */
    maxSourceSize() {
        return 0;
    }
    /** 素材は載せられない。**呼び出し側が分岐しなくて済むよう黙って無視する。** */
    setSource() {
        // 何もしない。UNSUPPORTED は mount 時に通知済み。
    }
    setTemplate(template) {
        this.template = template;
        this.resize();
    }
    setHighlight(ids) {
        this.highlighted = new Set(ids ?? []);
        this.resize();
    }
    resize() {
        if (this.stage === null || this.container === null || this.template === null)
            return;
        this.transform = computeTransform(measure(this.container), this.template.canvas, this.fit);
        applyTransform(this.stage, this.transform);
        setBackground(this.stage, this.template.backgroundUrl);
        renderMasks(this.stage, this.template.obstacles, this.transform, this.template.backgroundUrl, this.fit);
        /*
         * **枠ぜんぶを描く**(#33)。WebGL2 が使えない環境では素材を載せられず、
         * **空セルという概念が無い** —— #208 の「空のセルだけ」は WebGL 側の話で、
         * ここは従来どおり「どこに何が入るか」を示す。
         */
        renderOutlines(this.stage, byOrder(this.template.videoFrames).map((frame) => ({
            id: frame.id,
            points: quadToStage(frame.quad, this.transform),
        })), this.transform, this.highlighted);
        this.options?.onTransform?.(this.transform);
    }
    destroy() {
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        this.stage?.root.remove();
        this.stage = null;
        this.container = null;
        this.template = null;
        this.options = null;
    }
}
//# sourceMappingURL=fallback-renderer.js.map